/**
 * EPIC 47 — live hooks over the messaging + notifications subscriptions.
 * Mirror the useLeaveRequests.ts pattern (useState + onSnapshot). Used by the
 * worker inbox, the admin inbox, and the notification bell + nav badges.
 */

import type { AppNotification, Conversation, Message } from '@rendimees24/shared-types';
import { countConversationsUnreadFor } from '@rendimees24/shared-types';
import { useEffect, useMemo, useState } from 'react';

import { getMyClientWork } from './clientWork';
import {
  subscribeAllConversations,
  subscribeClientConversations,
  subscribeConversation,
  subscribeMessages,
  subscribeMyConversationReads,
  subscribeMyConversations,
} from './messaging';
import { subscribeMyNotifications } from './notifications';

export function useConversation(conversationId: string | undefined) {
  const [conversation, setConversation] = useState<Conversation | null>(null);

  useEffect(() => {
    if (!conversationId) {
      setConversation(null);
      return;
    }
    const unsub = subscribeConversation(conversationId, setConversation);
    return () => {
      try {
        unsub();
      } catch {
        /* ignore */
      }
    };
  }, [conversationId]);

  return conversation;
}

export function useMessages(conversationId: string | undefined) {
  const [snapshot, setSnapshot] = useState<{
    id: string;
    messages: Message[];
    error: Error | null;
  } | null>(null);
  useEffect(() => {
    if (!conversationId) return;
    return subscribeMessages(
      conversationId,
      (messages) => setSnapshot({ id: conversationId, messages, error: null }),
      (error) => setSnapshot({ id: conversationId, messages: [], error }),
    );
  }, [conversationId]);
  const current = snapshot?.id === conversationId ? snapshot : null;
  return {
    messages: current?.messages ?? [],
    loading: !!conversationId && !current,
    error: current?.error ?? null,
  };
}

/** Unified admin inbox (P1b) — every conversation type (worker/order/site). */
export function useAllConversations() {
  const [conversations, setConversations] = useState<Conversation[] | null>(null);

  useEffect(() => {
    const unsub = subscribeAllConversations(setConversations);
    return () => {
      try {
        unsub();
      } catch {
        /* ignore */
      }
    };
  }, []);

  return { conversations: conversations ?? [], loading: conversations === null };
}

/**
 * A non-staff user's OWN conversations (uid ∈ participantUids), most-recently-active
 * first. Backs the worker viewpoint of the app-bar MessagesMenu.
 */
export function useMyConversations(uid: string | undefined) {
  const [conversations, setConversations] = useState<Conversation[] | null>(null);

  useEffect(() => {
    if (!uid) {
      setConversations([]);
      return;
    }
    const unsub = subscribeMyConversations(uid, setConversations);
    return () => {
      try {
        unsub();
      } catch {
        /* ignore */
      }
    };
  }, [uid]);

  return { conversations: conversations ?? [], loading: conversations === null };
}

/** Total staff-side unread across ALL threads (admin nav badge). */
export function useStaffUnreadCount(): number {
  const { conversations } = useAllConversations();
  return useMemo(
    () => conversations.reduce((sum, c) => sum + (c.staffUnread ?? 0), 0),
    [conversations],
  );
}

export function useMyNotifications(uid: string | undefined) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  useEffect(() => {
    if (!uid) {
      setNotifications([]);
      return;
    }
    const unsub = subscribeMyNotifications(uid, setNotifications);
    return () => {
      try {
        unsub();
      } catch {
        /* ignore */
      }
    };
  }, [uid]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);
  return { notifications, unreadCount };
}

/**
 * Live inbox for a client company user (or a promoted object foreman — the
 * promote flow adds the clientId to their permissions, so the same query
 * serves both). Empty permissions yields an empty list rather than an error.
 */
export function useClientConversations(clientIds: readonly string[]) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  // Join the ids so the effect re-subscribes when the SET changes, not on every
  // render that hands back a fresh array with the same contents.
  const key = clientIds.join(',');
  useEffect(() => {
    setLoading(true);
    return subscribeClientConversations(
      key ? key.split(',') : [],
      (c) => {
        setConversations(c);
        setLoading(false);
      },
      // PB-13 — a narrowed company: read what the server says I may see.
      async (clientId) => {
        const work = await getMyClientWork(clientId);
        return [
          ...work.responsibilities.map((r) => r.siteId),
          ...work.company.sites.map((s) => s.siteId),
        ];
      },
    );
  }, [key]);
  return { conversations, loading };
}

/**
 * UX 7.1 — this person's own read marks (conversationId → lastReadAt). Null
 * until the first answer, so a badge never flashes the fallback count first.
 */
export function useMyConversationReads(uid: string | undefined) {
  const [reads, setReads] = useState<{ uid: string; map: Map<string, string> } | null>(null);
  useEffect(() => {
    if (!uid) return;
    return subscribeMyConversationReads(uid, (map) => setReads({ uid, map }));
  }, [uid]);
  return reads && reads.uid === uid ? reads.map : null;
}

/**
 * UX 7.1 (D11) — the client badge: how many of the company's threads are
 * unread FOR ME (not the company-shared `clientUnread`). Threads folded into
 * an order group's thread (7.3) are not counted twice.
 */
export function useClientPersonalUnreadCount(
  conversations: readonly Conversation[],
  uid: string | undefined,
): number {
  const reads = useMyConversationReads(uid);
  return useMemo(
    () => (uid && reads ? countConversationsUnreadFor(conversations, uid, reads) : 0),
    [conversations, uid, reads],
  );
}
